









































// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck Fix typing issues later
import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { required, requiredIf, email } from "vuelidate/lib/validators";
import {
  Dropdown,
  Input,
  FileUpload,
  Button,
  Datepicker,
  SvgIcon,
  Textbox,
} from "@brankas/web-ui";
import { namespace } from "vuex-class";

import { ClientInfo, IFileItem, PersonInfo, TFile, TFileUpload } from "@/types";
import { ETypeOfPIC, ETypeOfDoc } from "./helpers";
import { EUserRoles, EUserTypes } from "../index.dto";
import PICForm from "./PICForm.vue";
import { stringToNumberUtil, bytesToMegaBytes, parsePhoneNumber } from "@/utils/common-utils";
import { PICType } from "@/proto/identity/identity/all_pb";

export interface PersonInfoFormData extends PersonInfo {
  directorOfCompany: ["yes" | "no"];
  isSameWithBizOwner: boolean;
}

export interface FormData {
  authorizedSignatory: PersonInfoFormData[];
  billingPIC: PersonInfoFormData[];
}

const LIMIT_FILE_SIZE = 20; // 20mb

const clientModule = namespace("client");

const getInitialPersonInfo = (typeOfPIC: string): PersonInfoFormData => ({
  email: "",
  fullName: "",
  role: "",
  phoneNumber: "",
  phoneNumberCountryCode: "",
  idDoc: [],
  poaDoc: [],
  dirIdDoc: [],
  directorOfCompany: ["no"],
  isSameWithBizOwner: false,
  roleType:
    typeOfPIC === ETypeOfPIC.BILL
      ? PICType.BILLING
      : PICType.AUTHORIZED_SIGNATORY,
});
const initialFormData = (override = {}): FormData => ({
  authorizedSignatory: [],
  billingPIC: [],
  ...override,
});

const picValidation = {
  required: requiredIf(function () {
    return !this.isPersonal;
  }),
  $each: {
    email: {
      required,
      email,
    },
    fullName: {
      required,
    },
    role: {
      required,
    },
    phoneNumber: {
      required,
    },
    phoneNumberCountryCode: {
      required,
    },
    directorOfCompany: {},
    isSameWithBizOwner: {},
  },
};

@Component({
  components: {
    Dropdown,
    Input,
    FileUpload,
    Button,
    Datepicker,
    SvgIcon,
    Textbox,
    PICForm,
  },
  validations: {
    formData: {
      authorizedSignatory: picValidation,
      billingPIC: picValidation,
    },
  },
})
export default class EditPICForm extends Vue {
  @clientModule.State("currentClient") currentClient!: ClientInfo;
  @Prop({ type: Boolean, default: false }) loading!: boolean;

  get isPersonal() {
    return this.currentClient?.userType === EUserTypes.INDIVIDUAL;
  }

  readonly typeOfPIC = { ...ETypeOfPIC };
  formData: FormData = initialFormData();
  awaitingFormIdsToRemove: string[] = [];

  @Watch("currentClient", { immediate: true })
  watchCurrentClient(value: ClientInfo) {
    this._populateFormData(value);
  }

  get validateFile() {
    return [
      ...(!this.isPersonal ? this.formData.authorizedSignatory : []),
      ...this.formData.billingPIC,
    ].every((pic) => {
      const hasFile =
        (pic.idDoc as TFileUpload[]).length &&
        (pic.poaDoc as TFileUpload[]).length;

      if (pic.roleType === PICType.BILLING && this.isPersonal)
        return pic.isSameWithBizOwner ? true : hasFile;
      return pic.directorOfCompany[0] === "yes" ? true : hasFile;
    });
  }

  _populateFormData(value: ClientInfo) {
    if (!value) return;
    this.formData = {
      authorizedSignatory: this._mappingPersonFormData(
        value.authorizedSignatory
      ),
      billingPIC: this._mappingPersonFormData(value.financePIC),
    };
  }

  _mappingPersonFormData(personInfo?: PersonInfo[]): PersonInfoFormData[] {
    return (
      personInfo?.map((data) => {
        const poaDoc = [];
        const { fileName: fileNamePoaDoc, fileUrl: fileUrlPoaDoc } =
          data.poaDoc as IFileItem;
        if (fileNamePoaDoc && fileUrlPoaDoc)
          poaDoc.push({
            file: new File([], (data.poaDoc as IFileItem).fileName as string),
            signedUrl: (data.poaDoc as IFileItem).fileUrl,
          });

        const idDoc = [];
        const { fileName: fileNameIdDoc, fileUrl: fileUrlIdDoc } =
          data.idDoc as IFileItem;
        if (fileNameIdDoc && fileUrlIdDoc)
          idDoc.push({
            file: new File([], (data.idDoc as IFileItem).fileName as string),
            signedUrl: (data.idDoc as IFileItem).fileUrl,
          });

        const dirIdDoc = [];
        const { fileName: fileNameDirIdDoc, fileUrl: fileUrlDirIdDoc } =
          data.dirIdDoc as IFileItem;
        if (fileNameDirIdDoc && fileUrlDirIdDoc)
          dirIdDoc.push({
            file: new File([], (data.dirIdDoc as IFileItem).fileName as string),
            signedUrl: (data.dirIdDoc as IFileItem).fileUrl,
          });

        const { countryCode, phoneNumber } = parsePhoneNumber(
          data.phoneNumber,
          data.phoneNumberCountryCode
        );

        return {
          id: data.id,
          isDirector: data.isDirector,
          roleType: data.roleType,
          fullName: data.fullName,
          role: data.role,
          phoneNumber: phoneNumber,
          phoneNumberCountryCode: countryCode,
          email: data.email,
          poaDoc,
          idDoc,
          dirIdDoc,
          poaFileId: (data.poaDoc as IFileItem)?.fileId || "",
          identificationFileId: (data.idDoc as IFileItem)?.fileId || "",
          directorIdentificationFileId:
            (data.dirIdDoc as IFileItem)?.fileId || "",
          directorOfCompany: [data.isDirector ? "yes" : "no"],
          isSameWithBizOwner:
            this.isPersonal &&
            data.picIndex === 0 &&
            data.roleType === PICType.BILLING
              ? !!data.isDirector
              : false,
        };
      }) ?? []
    );
  }

  _validatePhoto(file: TFile): boolean {
    if (!file) return false;
    const sizeInMb = bytesToMegaBytes(file!.size, 2);

    const isIncluded = file!.type
      ? ["application/pdf"].includes(file!.type)
      : false;

    if (sizeInMb >= LIMIT_FILE_SIZE || !isIncluded) {
      return false;
    }

    return true;
  }

  onInputFile(files: TFile[], payload: string) {
    if (!payload) return;
    const [typeOfPic, type, index] = payload.split("-");
    let isValid = true;

    if (files.length) {
      isValid = this._validatePhoto(files[0]);
    }

    if (isValid) {
      this.formData[typeOfPic as ETypeOfPIC][+index][type as ETypeOfDoc] =
        files.map<TFileUpload>((file) => ({
          file,
        }));
    }
  }

  onInputPhone(value: string, payload: string) {
    if (!payload) return;
    const [typeOfPic, index] = payload.split("-");
    this.formData[typeOfPic as ETypeOfPIC][+index].phoneNumber =
      stringToNumberUtil(value);
  }

  onSubmit() {
    this.$v.$touch();
    if (this.$v.$invalid) return;

    this.$emit("submit", this.formData, this.awaitingFormIdsToRemove);
  }

  onAddForm(typeOfPIC: ETypeOfPIC) {
    this.formData[typeOfPIC].push(getInitialPersonInfo(typeOfPIC));
  }

  onRemoveForm(typeOfPIC: ETypeOfPIC, index = 0, formID = "") {
    if (formID) this.awaitingFormIdsToRemove.push(formID);

    this.formData[typeOfPIC].splice(index, 1);
  }

  onToggleBizOwnerAndDirector(
    val: boolean,
    payload: string,
    userRoles: EUserRoles
  ) {
    if (!payload) return;
    const [typeOfPic, index] = payload.split("-");
    if (val) this.formData[typeOfPic as ETypeOfPIC][+index].role = userRoles;
    if (userRoles === EUserRoles.BUSINESS_OWNER) {
      this.formData[typeOfPic as ETypeOfPIC][+index].fullName =
        this.currentClient.businessOwner?.fullname || "";
      this.formData[typeOfPic as ETypeOfPIC][+index].phoneNumber =
        this.currentClient.businessOwner?.phonenumber || "";
      this.formData[typeOfPic as ETypeOfPIC][+index].email =
        this.currentClient.businessOwner?.email || "";
    }
  }
}
